import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CustomText from 'components/common/CustomText/CustomText';
import { useGetCPTModifiers } from 'hooks/useLookups';
import { CPT, CPTModifier } from 'types/types/Types';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import AddIcon from 'icons/AddIcon';
import useStyles from './styles';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';

interface Props {
  title: string;
  selectedModifier: CPTModifier | null;
  setCPTModifier: (modifier: CPTModifier | null) => void;
  cpts: CPT[];
}

function SelectCPTModifier({
  title,
  selectedModifier,
  setCPTModifier,
  cpts,
}: Props) {
  const styles = useStyles();
  const globalStyles = useGlobalStyles();

  const [open, setOpen] = useState(false);
  const [modifiers, setModifiers] = useState<CPTModifier[]>([]);
  const [totalRVUs, setTotalRVUs] = useState(0);
  const [totalReimbursable, setTotalReimbursable] = useState(0);

  const { refetch } = useGetCPTModifiers({
    enabled: true,
    onSuccess: (data: CPTModifier[]) => setModifiers(data),
  });

  /* ---------------- RVU calculations ---------------- */

  const calculateRVUs = () => {
    let maxRvu = 0;

    const sumRvu = cpts.reduce(
      (sum, item) => sum + item.rvu_value * item.quantity,
      0
    );

    setTotalRVUs(sumRvu);

    for (let i = 0; i < cpts.length; i++) {
      if (cpts[i].rvu_value > maxRvu) {
        maxRvu = cpts[i].rvu_value;
      }
    }
    maxRvu = Number(maxRvu);
    const sumOfRest = sumRvu - maxRvu;
    const reimbursable = maxRvu + sumOfRest * 0.5;
    setTotalReimbursable(reimbursable);
  };

  useEffect(() => {
    calculateRVUs();
  }, [cpts, selectedModifier]);

  const totalAfterModifier = useMemo(() => {
    if (selectedModifier) {
      return (totalReimbursable * selectedModifier.percentage) / 100;
    }
    return 0;
  }, [selectedModifier, totalReimbursable]);

  /* ---------------- Modal logic ---------------- */

  const openModal = () => {
    setOpen(true);
    if (!modifiers.length) {
      refetch();
    }
  };

  const renderModifier = useCallback(
    (modifier: CPTModifier, index: number) => (
      <ListItem
        key={modifier.id}
        onClick={() => {
          setCPTModifier(modifier);
          setOpen(false);
        }}
        className={styles.resultItem}

      >
        <Box display="flex" width="100%" alignItems="center">
          <Box display="flex" width={40} alignItems="center" >
            <CustomText
              text={modifier.id}
              fontWeight={600}
              color={colors.black}
              textAlign={'center'}
              sx={{ width: '100%' }}
            />
          </Box>

          <Box flex={1} ml={1}>
            <CustomText
              text={`${modifier.percentage}%`}
              fontWeight={600}
              color={colors.black}
            />
            <CustomText
              text={modifier.description}
              color={colors.black}
            />
          </Box>
        </Box>
      </ListItem>
    ),
    [modifiers, setCPTModifier]
  );

  const renderSelectedModifier = (modifier: CPTModifier) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={1}
      m={2.5}
      border={`1px solid ${colors.itemBorder}`}
      borderRadius="10px"
    >
      <Box display="flex" flex={1}>
        <Box display="flex" width={40} alignItems="center" >
          <CustomText
            text={modifier.id}
            fontWeight={600}
            color={colors.black}
            textAlign={'center'}
            sx={{ width: '100%' }}
          />
        </Box>


        <Box ml={2}>
          <CustomText
            text={`${modifier.percentage}%`}
            fontWeight={600}
            color={colors.black}
          />
          <CustomText
            text={modifier.description}
            color={colors.black}
          />
        </Box>
      </Box>

      <IconButton
        color="error"
        onClick={() => setCPTModifier(null)}
      >
        <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );

  return (
    <>
      {/* Header */}
      <Box className={globalStyles.sectionContainer}>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={globalStyles.sectionHeader}
        >
          <CustomText
            text={title}
            color={colors.primary}
            fontWeight={600}
          />

          <Box mr={2} gap={1} display="flex">
            <Tooltip title={"Add CPTs"}>
              <IconButton
                onClick={openModal}
                className={globalStyles.sectionButton}
              >
                {selectedModifier ? (<DriveFileRenameOutlineIcon
                    sx={{ fontSize: 22, color: colors.primary }}
                  />): (<AddIcon size = { 22 } color = {colors.primary} />)}
              </IconButton>
            </Tooltip>
          </Box>

        </Stack>


        {/* Totals */}
        {selectedModifier && totalAfterModifier > 0 && (
          <>
            <Divider />
            <Stack px={2.5} pt={2} spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <CustomText text="Total Reimbursable RVUs After CPT Modifier" color={colors.black} fontWeight={600} fontSize={'15px'} sx={{ maxWidth: '80%' }} />
                <CustomText text={totalAfterModifier.toFixed(3)} color={colors.black} fontWeight={600} fontSize={'15px'} />
              </Stack>

            </Stack>
          </>
        )}


        {/* Selected modifier */}
        {selectedModifier && renderSelectedModifier(selectedModifier)}

      </Box>


      {/* Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          <List dense>
            {modifiers.map((modifier, index) =>
              renderModifier(modifier, index)
            )}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default memo(SelectCPTModifier);
